import * as Sentry from '@sentry/react'
import { useEffect } from 'react'
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType
} from 'react-router-dom'

// Environment configuration
const environment = import.meta.env.VITE_ENVIRONMENT || 'local'
const dsn = import.meta.env.VITE_SENTRY_DSN
const release = import.meta.env.VITE_APP_VERSION || 'unknown'

export const initializeSentry = () => {
  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn('Sentry DSN not configured. Skipping Sentry initialization.')
    return
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Enable debug mode to see what Sentry is doing
    debug: environment !== 'production',

    // Maximum number of breadcrumbs (logs) to keep
    maxBreadcrumbs: 100,

    // Performance Monitoring
    integrations: [
      // React Router integration for better error tracking
      Sentry.reactRouterV7BrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes
      }),

      // Session Replay - captures user interactions when errors occur
      Sentry.replayIntegration({
        // Mask all text content by default for privacy
        maskAllText: true,
        blockAllMedia: true
      }),

      // Feedback integration for user reports
      Sentry.feedbackIntegration({
        colorScheme: 'light',
        autoInject: false, // We'll trigger manually
        showBranding: false,
        formTitle: 'Laporkan Masalah',
        submitButtonLabel: 'Kirim Laporan',
        cancelButtonLabel: 'Batal',
        confirmButtonLabel: 'Konfirmasi',
        addScreenshotButtonLabel: 'Tambahkan Screenshot',
        removeScreenshotButtonLabel: 'Hapus Screenshot',
        nameLabel: 'Nama',
        namePlaceholder: 'Nama Anda',
        emailLabel: 'Email',
        emailPlaceholder: 'email@example.com',
        isRequiredLabel: '(wajib)',
        messageLabel: 'Deskripsi Masalah',
        messagePlaceholder: 'Jelaskan masalah yang Anda alami...',
        successMessageText: 'Terima kasih atas laporannya!'
      }),

      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration(),

      // Browser profiling for performance analysis
      Sentry.browserProfilingIntegration(),

      // Breadcrumbs integration for console logs
      Sentry.breadcrumbsIntegration({
        console: true, // Capture console.log, console.warn, console.error
        dom: true, // Capture DOM events (clicks, inputs, etc.)
        fetch: true, // Capture fetch/XHR requests
        history: true, // Capture navigation history
        sentry: true, // Capture Sentry events
        xhr: true // Capture XMLHttpRequest
      }),

      // Additional context integrations
      Sentry.extraErrorDataIntegration(),
      Sentry.httpClientIntegration(),
      Sentry.reportingObserverIntegration()
    ],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // Adjust this in production
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Set profilesSampleRate to profile 10% of transactions
    profilesSampleRate: environment === 'production' ? 0.1 : 1.0,

    // Replay sample rates
    replaysSessionSampleRate: environment === 'production' ? 0.1 : 0.5,
    replaysOnErrorSampleRate: 1.0,

    // Capture unhandled promise rejections
    beforeSend(event, hint) {
      // Filter out errors in development if needed
      if (environment === 'local' && import.meta.env.DEV) {
        console.log('Sentry Event (Local):', event, hint)
        return null // Don't send in local dev
      }

      // Check if it's a chunk loading error
      const error = hint.originalException
      if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string'
      ) {
        if (
          error.message.includes('Failed to fetch dynamically imported module')
        ) {
          event.fingerprint = ['chunk-load-error']
          event.tags = {
            ...event.tags,
            error_type: 'chunk_loading'
          }
        }
      }

      return event
    },

    // Ignore certain errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network errors that are expected
      'NetworkError',
      'Failed to fetch',
      // Random plugins/extensions
      'atomicFindClose',
      // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
      'originalCreateNotification',
      'canvas.contentDocument',
      '__gCrWeb',
      'ConduitPage',
      'SecurityError'
    ],

    // Don't report errors from these URLs
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i
    ],

    // Add user context
    initialScope: {
      tags: {
        environment
      }
    }
  })

  // Set user context if available
  const userInfo = getUserFromStorage()
  if (userInfo) {
    Sentry.setUser({
      id: userInfo.id,
      email: userInfo.email,
      username: userInfo.username
    })
  }
}

// Helper to get user from Redux persist storage
function getUserFromStorage() {
  try {
    const persistRoot = localStorage.getItem('persist:root')
    if (persistRoot) {
      const parsed = JSON.parse(persistRoot)
      const authState = parsed.auth ? JSON.parse(parsed.auth) : null
      return authState?.user || null
    }
  } catch (error) {
    console.error('Failed to get user from storage:', error)
  }
  return null
}

// Export Sentry for use in components
export { Sentry }

// Helper function to show feedback dialog
export const showFeedbackDialog = () => {
  const feedback = Sentry.getFeedback()
  if (feedback) {
    feedback.createForm()
    feedback.attachTo(document.body)
  }
}

// Helper to capture exception with context
export const captureException = (
  error: Error,
  context?: Record<string, any>
) => {
  Sentry.captureException(error, {
    contexts: {
      custom: context
    }
  })
}

// Helper to set user context
export const setUserContext = (user: {
  id: string
  email?: string
  username?: string
}) => {
  Sentry.setUser(user)
}

// Helper to clear user context (on logout)
export const clearUserContext = () => {
  Sentry.setUser(null)
}
